import { Button } from '@/components/ui/button';
import type { Subject, SubjectField } from './api';
import { fieldSummary, suggestion } from './fields';

/**
 * The inputs for a subject's own fields (a list to choose from, a number, or free text), plus a one-click
 * suggestion for a lesson that has no values yet. Changes are reported per field; an empty value clears it.
 */
export default function FieldInputs(props: {
  subject: Subject;
  values: Record<string, string> | undefined;
  onChange: (values: Record<string, string>) => void;
}) {
  const { subject, values, onChange } = props;
  if (subject.fields.length === 0) return null;
  const proposed = suggestion(subject, values);
  const proposedText = fieldSummary(subject, proposed);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {subject.fields.map((field) => (
        <FieldInput key={field.id} field={field} value={values?.[field.id] ?? ''} onChange={(value) => onChange({ [field.id]: value })} />
      ))}
      {proposedText && (
        <Button type="button" variant="link" className="h-auto min-h-11 px-0 text-sm" onClick={() => onChange(proposed)}>
          Voorstel: {proposedText}
        </Button>
      )}
    </div>
  );
}

function FieldInput({ field, value, onChange }: { field: SubjectField; value: string; onChange: (value: string) => void }) {
  if (field.kind === 'choice') {
    return (
      <select aria-label={field.label} value={value} onChange={(event) => onChange(event.target.value)} className="min-h-11 rounded border px-2">
        <option value="">{field.label}</option>
        {/* A value that is no longer in the list (the list was edited) stays visible instead of silently vanishing. */}
        {value && !field.options.includes(value) && <option value={value}>{value}</option>}
        {field.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  const save = (text: string) => text.trim() !== value && onChange(text.trim());
  return (
    <input
      key={value}
      aria-label={field.label}
      placeholder={field.label}
      defaultValue={value}
      type={field.kind === 'number' ? 'number' : 'text'}
      {...(field.kind === 'number' ? { min: field.min, max: field.max } : { maxLength: 100 })}
      onBlur={(event) => save(event.target.value)}
      onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
      className={field.kind === 'number' ? 'w-28' : 'w-44'}
    />
  );
}
